import { 
  LearningPath, 
  LearningNode, 
  LearningCategory, 
  ValidationCriteria,
  NodeStatus,
  Milestone
} from '../types/learning-path';
import { ConceptCategory, Difficulty } from '../types/index';

// Validation criteria templates
const BASIC_VALIDATION: ValidationCriteria = {
  minimumScore: 70,
  requiredAttempts: 2,
  mustDemonstrate: ['correct_syntax', 'basic_logic']
};

const INTERMEDIATE_VALIDATION: ValidationCriteria = {
  minimumScore: 75,
  requiredAttempts: 3,
  mustDemonstrate: ['correct_syntax', 'proper_unity_api_usage', 'clean_code']
};

const ADVANCED_VALIDATION: ValidationCriteria = {
  minimumScore: 80,
  requiredAttempts: 3,
  mustDemonstrate: ['correct_syntax', 'advanced_concepts', 'optimization_awareness']
};

// ============================================================================
// LEARNING NODES DEFINITIONS
// ============================================================================

const CSHARP_FOUNDATIONS: LearningNode[] = [
  {
    id: 'variables_and_types',
    title: 'Variables et Types de Base',
    description: 'Maîtrisez int, string, bool, float - les fondations de tout programme C#',
    category: LearningCategory.CSHARP_BASICS,
    prerequisites: [],
    concepts: [ConceptCategory.DATA_TYPES, ConceptCategory.VARIABLES],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 15,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'operators_and_expressions',
    title: 'Opérateurs et Expressions',
    description: 'Calculs, comparaisons, logique - construire des expressions complexes',
    category: LearningCategory.CSHARP_BASICS,
    prerequisites: ['variables_and_types'],
    concepts: [ConceptCategory.OPERATORS, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 20,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'conditional_logic',
    title: 'Logique Conditionnelle',
    description: 'if, else if, else - faire prendre des décisions à votre code',
    category: LearningCategory.CSHARP_CONTROL_FLOW,
    prerequisites: ['operators_and_expressions'],
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 25,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'loops_iteration',
    title: 'Boucles et Itération',
    description: 'for, foreach - répéter des actions efficacement',
    category: LearningCategory.CSHARP_CONTROL_FLOW,
    prerequisites: ['conditional_logic'],
    concepts: [ConceptCategory.LOOPS, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 30,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'methods_basics',
    title: 'Méthodes et Fonctions',
    description: 'Organiser votre code avec des méthodes - paramètres, valeurs de retour',
    category: LearningCategory.CSHARP_BASICS,
    prerequisites: ['loops_iteration'],
    concepts: [ConceptCategory.METHODS, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 35,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'arrays_and_lists',
    title: 'Tableaux et Collections',
    description: 'Gérer des groupes de données avec arrays et List<T>',
    category: LearningCategory.CSHARP_DATA_STRUCTURES,
    prerequisites: ['methods_basics'],
    concepts: [ConceptCategory.DATA_TYPES, ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  }
];

const UNITY_FOUNDATIONS: LearningNode[] = [
  {
    // L'ancien « Éditeur Unity - Premiers Pas » (Hiérarchie, Inspector à la
    // souris) n'est pas exerçable dans un éditeur web. Le nœud garde son id (rien à recâbler) mais
    // enseigne l'anatomie d'un MonoBehaviour — ce que l'examen Unity Certified User teste vraiment.
    id: 'unity_editor_basics',
    title: 'Premier Script Unity',
    description: 'Anatomie d\'un MonoBehaviour : using UnityEngine, la classe, le squelette Start/Update, Debug.Log, champs visibles dans l\'Inspector et attributs',
    category: LearningCategory.UNITY_EDITOR,
    prerequisites: ['methods_basics'], // C# methods needed for scripts
    concepts: [ConceptCategory.DEBUGGING, ConceptCategory.NAMING_CONVENTIONS, ConceptCategory.MONOBEHAVIOUR_LIFECYCLE],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 25,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'gameobject_transform',
    title: 'GameObjects et Transform',
    description: 'Manipuler position, rotation, échelle - les bases de tout objet Unity',
    category: LearningCategory.UNITY_GAMEOBJECTS,
    prerequisites: ['unity_editor_basics', 'variables_and_types'],
    concepts: [ConceptCategory.GAMEOBJECT_MANIPULATION, ConceptCategory.VECTOR_MATH],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 30,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'monobehaviour_lifecycle',
    title: 'Cycle de Vie MonoBehaviour',
    description: 'Awake(), Start(), Update() - quand et pourquoi utiliser chaque méthode',
    category: LearningCategory.UNITY_LIFECYCLE,
    prerequisites: ['gameobject_transform'],
    concepts: [ConceptCategory.MONOBEHAVIOUR_LIFECYCLE],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 35,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'input_handling',
    title: 'Gestion des Entrées',
    description: 'Clavier et souris - rendre votre jeu interactif',
    category: LearningCategory.UNITY_INPUT,
    prerequisites: ['monobehaviour_lifecycle', 'conditional_logic'],
    concepts: [ConceptCategory.INPUT_HANDLING, ConceptCategory.CONDITIONALS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 30,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'component_access',
    title: 'Accès aux Composants',
    description: 'GetComponent<T>() - communiquer entre scripts et composants',
    category: LearningCategory.UNITY_GAMEOBJECTS,
    prerequisites: ['input_handling'],
    concepts: [ConceptCategory.COMPONENT_ACCESS],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 40,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  }
];

const UNITY_INTERMEDIATE: LearningNode[] = [
  {
    id: 'physics_rigidbody',
    title: 'Physique et Rigidbody',
    description: 'Mouvement réaliste avec Rigidbody2D, linearVelocity, forces et physique 2D',
    category: LearningCategory.UNITY_PHYSICS,
    prerequisites: ['component_access', 'vectors_math'],
    concepts: [ConceptCategory.PHYSICS_RIGIDBODY, ConceptCategory.VECTOR_MATH],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 45,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'collision_detection',
    title: 'Détection de Collision',
    description: 'OnCollisionEnter2D(), OnTriggerEnter2D() - réagir aux interactions',
    category: LearningCategory.UNITY_PHYSICS,
    prerequisites: ['physics_rigidbody'],
    concepts: [ConceptCategory.PHYSICS_RIGIDBODY, ConceptCategory.MONOBEHAVIOUR_LIFECYCLE],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 50,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'basic_coroutines',
    title: 'Coroutines de Base',
    description: 'IEnumerator et yield - gérer le temps et les séquences',
    category: LearningCategory.UNITY_ADVANCED,
    prerequisites: ['collision_detection', 'arrays_and_lists'],
    concepts: [ConceptCategory.COROUTINES, ConceptCategory.METHODS],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 55,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'animation_system',
    title: 'Système d\'Animation',
    description: 'Animator Controller - donner vie à vos objets',
    category: LearningCategory.UNITY_ANIMATION,
    prerequisites: ['basic_coroutines'],
    concepts: [ConceptCategory.ANIMATION_SYSTEM],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 60,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'ui_interaction',
    title: 'Interface Utilisateur',
    description: 'Canvas, Button, Text - créer des interfaces utilisateur',
    category: LearningCategory.UNITY_UI,
    prerequisites: ['animation_system'],
    concepts: [ConceptCategory.UI_INTERACTION],
    difficulty: Difficulty.INTERMEDIATE,
    estimatedTime: 50,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 8
  }
];

// Helper node for vector math (should be inserted appropriately)
const VECTORS_MATH: LearningNode = {
  id: 'vectors_math',
  title: 'Mathématiques Vectorielles',
  description: 'Vector3, Vector2 - les mathématiques derrière le mouvement',
  category: LearningCategory.UNITY_GAMEOBJECTS,
  prerequisites: ['operators_and_expressions'],
  concepts: [ConceptCategory.VECTOR_MATH, ConceptCategory.DATA_TYPES],
  difficulty: Difficulty.BEGINNER,
  estimatedTime: 35,
  validationCriteria: BASIC_VALIDATION,
  requiredSuccessfulExercises: 8
};

// Objectif « X/N exercices réussis » d'un nœud qui ne déclarerait pas `requiredSuccessfulExercises`.
// Même valeur côté affichage (frontend LearningPath.tsx) et côté complétion réelle du nœud
// (backend getNodeRequirements, routes/exercises.ts).
export const DEFAULT_REQUIRED_SUCCESSFUL_EXERCISES = 8;

// ============================================================================
// ALL_NODES — LA source de vérité du curriculum Unity (17 nœuds), dans l'ordre pédagogique
// (= `orderIndex` 1..17 du seed backend/src/data/knowledge-points.json).
// Consommé par : LearningPath.tsx (affichage), HomePage.tsx, backend/src/utils/curriculum.ts
// (prérequis + nœuds de départ), routes/exercises.ts (suggestion). Ne jamais recopier ailleurs.
// ============================================================================

export const ALL_NODES = [
  ...CSHARP_FOUNDATIONS,
  VECTORS_MATH,
  ...UNITY_FOUNDATIONS,
  ...UNITY_INTERMEDIATE
];

// ============================================================================
// UNITY MILESTONES — un jalon (badge) par nœud, dans l'ordre pédagogique des 17 nœuds
// `requiredNodes` = le nœud + ses prérequis transitifs.
// Ce sont les `milestones` de BEGINNER_PATH, le parcours
// Unity affiché aux étudiants (frontend/src/components/LearningPath.tsx importe BEGINNER_PATH).
// ============================================================================

export const UNITY_MILESTONES: Milestone[] = [
  {
    id: 'unity_variables_mastery',
    name: 'Premières Variables',
    description: 'Tu sais déclarer et utiliser int, float, string et bool dans un script Unity',
    requiredNodes: ['variables_and_types'],
    reward: 'Badge Premières Variables'
  },
  {
    id: 'unity_operators_mastery',
    name: 'Calculateur C#',
    description: 'Tu sais construire des calculs, des comparaisons et des expressions logiques',
    requiredNodes: ['variables_and_types', 'operators_and_expressions'],
    reward: 'Badge Calculateur C#'
  },
  {
    id: 'unity_conditions_mastery',
    name: 'Maître des Conditions',
    description: 'Tu sais faire prendre des décisions à ton code avec if, else, switch et les enums',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic'],
    reward: 'Badge Maître des Conditions'
  },
  {
    id: 'unity_loops_mastery',
    name: 'Maître des Boucles',
    description: 'Tu sais répéter des actions avec for, while et foreach sans figer Unity',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration'],
    reward: 'Badge Maître des Boucles'
  },
  {
    id: 'unity_methods_mastery',
    name: 'Architecte des Méthodes',
    description: 'Tu sais découper ton code en méthodes avec paramètres et valeurs de retour',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics'],
    reward: 'Badge Architecte des Méthodes'
  },
  {
    id: 'unity_collections_mastery',
    name: 'Gestionnaire de Collections',
    description: 'Tu sais stocker et parcourir des groupes de données avec les tableaux, List<T> et Dictionary',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'arrays_and_lists'],
    reward: 'Badge Gestionnaire de Collections'
  },
  {
    id: 'unity_vectors_mastery',
    name: 'Maître des Vecteurs',
    description: 'Tu sais manipuler des Vector3 : directions, distances, normalisation et interpolation',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'vectors_math'],
    reward: 'Badge Maître des Vecteurs'
  },
  {
    id: 'unity_first_script_mastery',
    name: 'Scripteur Unity',
    description: 'Tu sais écrire un MonoBehaviour propre : using, classe, Start/Update et Debug.Log',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'unity_editor_basics'],
    reward: 'Badge Scripteur Unity'
  },
  {
    id: 'unity_transform_mastery',
    name: 'Pilote du Transform',
    description: 'Tu sais déplacer, tourner et redimensionner des GameObjects, les instancier et les détruire',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'unity_editor_basics', 'gameobject_transform'],
    reward: 'Badge Pilote du Transform'
  },
  {
    id: 'unity_lifecycle_mastery',
    name: 'Maître du Temps',
    description: 'Tu sais quand utiliser Awake, Start, Update et FixedUpdate, et rendre le mouvement indépendant des FPS avec Time.deltaTime',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle'],
    reward: 'Badge Maître du Temps'
  },
  {
    id: 'unity_input_mastery',
    name: 'Maître des Commandes',
    description: 'Tu sais lire le clavier et la souris avec Input pour rendre ton jeu interactif',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling'],
    reward: 'Badge Maître des Commandes'
  },
  {
    id: 'unity_components_mastery',
    name: 'Connecteur de Composants',
    description: 'Tu sais récupérer des composants avec GetComponent et faire communiquer tes scripts sans NullReferenceException',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access'],
    reward: 'Badge Connecteur de Composants'
  },
  {
    id: 'unity_physics_mastery',
    name: 'Physicien du Jeu',
    description: 'Tu sais faire bouger un Rigidbody2D avec linearVelocity, AddForce et MovePosition dans FixedUpdate',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'vectors_math', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access', 'physics_rigidbody'],
    reward: 'Badge Physicien du Jeu'
  },
  {
    id: 'unity_collisions_mastery',
    name: 'Détecteur de Collisions',
    description: 'Tu sais réagir aux collisions et aux triggers avec la bonne signature et CompareTag',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'vectors_math', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access', 'physics_rigidbody', 'collision_detection'],
    reward: 'Badge Détecteur de Collisions'
  },
  {
    id: 'unity_coroutines_mastery',
    name: 'Chorégraphe des Coroutines',
    description: 'Tu sais séquencer des actions dans le temps avec IEnumerator, StartCoroutine et yield',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'arrays_and_lists', 'vectors_math', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access', 'physics_rigidbody', 'collision_detection', 'basic_coroutines'],
    reward: 'Badge Chorégraphe des Coroutines'
  },
  {
    id: 'unity_animation_mastery',
    name: 'Animateur',
    description: 'Tu sais piloter un Animator par code : SetBool, SetTrigger, Play et les événements d\'animation',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'arrays_and_lists', 'vectors_math', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access', 'physics_rigidbody', 'collision_detection', 'basic_coroutines', 'animation_system'],
    reward: 'Badge Animateur'
  },
  {
    id: 'unity_ui_mastery',
    name: 'Architecte d\'Interface',
    description: 'Tu sais relier l\'interface au jeu : textes, boutons, barres de vie et panneaux — le parcours Unity complet est terminé',
    requiredNodes: ['variables_and_types', 'operators_and_expressions', 'conditional_logic', 'loops_iteration', 'methods_basics', 'arrays_and_lists', 'vectors_math', 'unity_editor_basics', 'gameobject_transform', 'monobehaviour_lifecycle', 'input_handling', 'component_access', 'physics_rigidbody', 'collision_detection', 'basic_coroutines', 'animation_system', 'ui_interaction'],
    reward: 'Badge Architecte d\'Interface'
  }
];

// ============================================================================
// LEARNING PATHS DEFINITIONS  
// ============================================================================

// Parcours Unity AFFICHÉ aux étudiants : les 17 nœuds d'ALL_NODES et les
// 17 jalons UNITY_MILESTONES. Avant : 14 nœuds (sans coroutines/animation/UI) et 3 jalons, et de
// toute façon une copie inline à 3 nœuds dans LearningPath.tsx décidait de l'affichage.
export const BEGINNER_PATH: LearningPath = {
  id: 'beginner_unity_programmer',
  name: 'Programmeur Unity Débutant',
  description: 'Parcours complet en 17 étapes : les fondations du C# (variables, conditions, boucles, méthodes, collections), puis Unity — scripts, GameObjects, cycle de vie, entrées, composants, physique, collisions, coroutines, animation et interface.',
  targetAudience: 'Débutants complets, étudiants en reconversion',
  estimatedDuration: '4-6 mois',
  nodes: ALL_NODES,
  milestones: UNITY_MILESTONES
};

export const INTERMEDIATE_PATH: LearningPath = {
  id: 'intermediate_game_developer', 
  name: 'Développeur de Jeux Intermédiaire',
  description: 'Approfondissez vos compétences Unity avec des concepts avancés et des systèmes complexes.',
  targetAudience: 'Développeurs avec les bases Unity acquises',
  estimatedDuration: '3-4 semaines',
  nodes: [
    ...UNITY_INTERMEDIATE,
    {
      id: 'advanced_coroutines',
      title: 'Coroutines Avancées',
      description: 'Coroutines avec paramètres, gestion complexe du temps',
      category: LearningCategory.UNITY_ADVANCED,
      prerequisites: ['basic_coroutines'],
      concepts: [ConceptCategory.COROUTINES, ConceptCategory.METHODS],
      difficulty: Difficulty.ADVANCED,
      estimatedTime: 65,
      validationCriteria: ADVANCED_VALIDATION
    },
    {
      id: 'game_architecture',
      title: 'Architecture de Jeu',
      description: 'Organiser le code, patterns de conception, bonnes pratiques',
      category: LearningCategory.GAME_ARCHITECTURE,
      prerequisites: ['ui_interaction', 'advanced_coroutines'],
      concepts: [ConceptCategory.METHODS, ConceptCategory.ACCESS_MODIFIERS],
      difficulty: Difficulty.ADVANCED,
      estimatedTime: 70,
      validationCriteria: ADVANCED_VALIDATION
    }
  ],
  milestones: [
    {
      id: 'interactive_systems_master',
      name: 'Maître des Systèmes Interactifs',
      description: 'Vous maîtrisez physique, collisions et interactions complexes',
      requiredNodes: ['physics_rigidbody', 'collision_detection', 'basic_coroutines'],
      reward: 'Badge Systèmes Interactifs'
    },
    {
      id: 'complete_game_developer',
      name: 'Développeur de Jeu Complet',
      description: 'Vous pouvez créer des jeux complets avec UI, animation et architecture propre',
      requiredNodes: ['animation_system', 'ui_interaction', 'advanced_coroutines', 'game_architecture'],
      reward: 'Badge Développeur Expert',
      unlocksPath: 'certification_preparation'
    }
  ]
};

export const CERTIFICATION_PATH: LearningPath = {
  id: 'certification_preparation',
  name: 'Préparation Certification Unity',
  description: 'Préparez-vous à l\'examen Unity User: Programmer avec des exercices ciblés.',
  targetAudience: 'Développeurs visant la certification officielle Unity',
  estimatedDuration: '2-3 semaines',
  nodes: [
    // Specialized certification-focused nodes would go here
    // These would cover the specific topics mentioned in the certification requirements
  ],
  milestones: [
    {
      id: 'certification_ready',
      name: 'Prêt pour Certification',
      description: 'Vous êtes prêt à passer l\'examen Unity User: Programmer',
      requiredNodes: [], // Would be populated with certification nodes
      reward: 'Certificat de Préparation Unity'
    }
  ]
};

export const ALL_LEARNING_PATHS = [
  BEGINNER_PATH,
  INTERMEDIATE_PATH,
  CERTIFICATION_PATH
];

